/* eslint-disable no-shadow */
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import haversine from 'haversine';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
  getTranslation,
  getVenueSearchThunk,
  Icon,
  utils,
} from '@resy/ui-kitchen';
import Cookies from '../../../../../ui-kitchen/src/providers/Cookies';

import './SearchContainer.scss';

import SearchResultsContainer from '../SearchResultsContainer';
import {
  getMarketLocationThunk,
  setSelectedLocationThunk,
} from '../../../../../ui-kitchen/src/reducers/resyLocationsSlice';
import {
  ALL_DAY,
  ISO_DATE_FORMAT,
} from '../../../../../ui-kitchen/src/constants/resyCore';
import {
  selectIsAuthenticated,
} from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import {
  LIST_ID,
} from '../../../../../ui-kitchen/src/components/SearchFilterContainer/searchFilterConstants';
import {
  loadCuisinesThunk,
} from '../../../../../ui-kitchen/src/reducers/venueSearchSlice';
import {
  getMultipleLists,
  resetFavorites,
} from '../../../../../ui-kitchen/src/reducers/favoriteSlice';
import { selectExperiment, selectExperimentsReady } from '../../../../../ui-kitchen/src/components/Experiments/useExperiment';
import { RESY_WEB_ENABLE_CUISINE_AGGREGATION_FILTER } from '../../../../../ui-kitchen/src/constants/optimizelyExperiments';
import FavoriteHeart
  from '../../../../../ui-kitchen/src/svg/icons/favorite_heart.svg';
import KnifeFork from '../../../../../ui-kitchen/src/svg/icons/knife-fork.svg';
import Table from '../../../../../ui-kitchen/src/svg/icons/table.svg';
import {
  getImageUrl,
  getNumOfVenuesContent,
  Logger,
} from '../../../../../ui-kitchen/src/utils';
import {
  setFunctionalCookie,
} from '../../../../../ui-kitchen/src/utils/resyUCMCookieUtils';

import ABTestWithFallback from '../../../../../ui-kitchen/src/components/Experiments/ABTestWithFallback';
import Variation from '../../../../../ui-kitchen/src/components/Experiments/Variation';
import DeprecatedMapContainer from '../MapContainer/DeprecatedMapContainer';
import MapContainer from '../MapContainer/MapContainer';

const GDA_URL = '/global-dining-access';
const QUERY_TYPE = { facet: 'facet', query: 'query' };
const FACET_TYPE = { list: LIST_ID };
const SEARCH_ITEM_LINK_CLASS = 'SearchResult__container-link';

// used to cleanup ucm cookie listener
let userLocationMapCookieCleanup = () => {
};
let initActiveViewCookieCleanup = () => {
};
let setActiveViewCookieCleanup = () => {
};
let toggleActiveViewCookieCleanup = () => {
};

const searchContainerPropTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
    $on: PropTypes.func.isRequired,
    metadata: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
    }),
    nav: PropTypes.shape({
      backPath: PropTypes.string,
    }),
  }).isRequired,
  $window: PropTypes.shape({}).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func.isRequired,
  }).isRequired,
  areExperimentsReady: PropTypes.bool,
  collectionData: PropTypes.arrayOf(PropTypes.shape({})),
  favoriteVenueIds: PropTypes.arrayOf(PropTypes.number),
  getMarketLocationThunk: PropTypes.func.isRequired,
  getMultipleLists: PropTypes.func.isRequired,
  getQueuedData: PropTypes.func.isRequired,
  getVenueSearchThunk: PropTypes.func.isRequired,
  hits: PropTypes.arrayOf(PropTypes.shape({})),
  isAuthenticated: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  isSearching: PropTypes.bool,
  lists: PropTypes.arrayOf(PropTypes.shape({})),
  cuisines: PropTypes.arrayOf(PropTypes.string),
  loadCuisinesThunk: PropTypes.func,
  useCuisineAggregationFilter: PropTypes.bool,
  nbPages: PropTypes.number,
  nearbyTranslation: PropTypes.string.isRequired,
  onClear: PropTypes.func.isRequired,
  onPillSelect: PropTypes.func.isRequired,
  onVenueSelect: PropTypes.func.isRequired,
  query: PropTypes.shape({
    type: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
    ]).isRequired,
  }).isRequired,
  reservationLimit: PropTypes.number.isRequired,
  resetFavorites: PropTypes.func.isRequired,
  searchSessionId: PropTypes.string,
  selectedCoreISODate: PropTypes.string.isRequired,
  selectedCoreSeats: PropTypes.number.isRequired,
  selectedDate: PropTypes.string,
  selectedTime: PropTypes.string,
  selectedLocation: PropTypes.shape({
    code: PropTypes.string.isRequired,
    map_center: PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
    }),
    name: PropTypes.string.isRequired,
    radius: PropTypes.number.isRequired,
    url_slug: PropTypes.string.isRequired,
  }).isRequired,
  selectedPartySize: PropTypes.number,
  setQueuedData: PropTypes.func.isRequired,
  setSelectedLocationThunk: PropTypes.func.isRequired,
  showAuthModal: PropTypes.func.isRequired,
  store: PropTypes.shape({
    dispatch: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
  }).isRequired,
  userLocation: PropTypes.shape({
    lat: PropTypes.number,
    long: PropTypes.number,
  }).isRequired,
  venueSearchError: PropTypes.shape({}),
  venueSearchStatus: PropTypes.string,
};

class SearchContainer extends React.Component {
  geolocationOptions = {
    maximumAge: 5 * 60 * 1000, // 5 minutes
    timeout: 10 * 1000, // 10 seconds
  }

  static propTypes = searchContainerPropTypes

  static defaultProps = {
    collectionData: [],
    cuisines: [],
    loadCuisinesThunk: undefined,
    areExperimentsReady: true,
    useCuisineAggregationFilter: false,
    favoriteVenueIds: [],
    hits: [],
    isMobile: true,
    isSearching: false,
    lists: undefined,
    nbPages: 0,
    searchSessionId: '',
    selectedDate: null,
    selectedTime: ALL_DAY,
    selectedPartySize: null,
    venueSearchError: null,
    venueSearchStatus: 'idle',
  }

  state = {
    activeView: 'list',
    boundingBox: null,
    center: null,
    cuisines: [],
    error: null,
    hasGeolocationControlBeenClicked: false,
    hitsPerPage: 20,
    hoveredVenue: null,
    isGeolocationActive: false,
    isGeolocationBlocked: false,
    isGeolocationLoading: false,
    isLoading: true,
    locationMode: 'location',
    page: 1,
    selectedCollection: null,
    selectedVenue: null,
    hasUserInteractedWithMap: false,
    shouldShowRedo: false,
    shouldUseBoundingBox: false,
    pendingInitialSearch: false,
    urlSearchParams: new URLSearchParams(window.location.search).toString(),
    userLocation: null,
    zoom: null,
  }

  isGeolocationSupported = !!navigator.geolocation;

  experimentsHydrationTimeout = null;

  hasExperimentGateTimedOut = false;

  async componentDidMount() {
    const {
      areExperimentsReady,
      $rootScope,
      getMultipleLists,
      isAuthenticated,
      isMobile,
      loadCuisinesThunk: fetchCuisines,
      resetFavorites,
      selectedLocation,
      useCuisineAggregationFilter,
      userLocation,
    } = this.props;

    $rootScope.$on('SEARCH_QUERY_TRIGGERED', this.setViewToList);

    if (areExperimentsReady) {
      this.assessLocation();
    } else {
      this.setState({ pendingInitialSearch: true });
      this.experimentsHydrationTimeout = setTimeout(() => {
        this.hasExperimentGateTimedOut = true;
        this.setState({ pendingInitialSearch: false }, this.assessLocation);
      }, 2000);
    }

    if (isMobile) {
      this.handleMobile();
    } else {
      this.handleDesktop();
    }

    const searchInput = document.getElementsByClassName(
      'react-autosuggest__input'
    );

    if (searchInput.length > 0) {
      searchInput[0].blur();
    }

    setTimeout(this.setScrollListener, 1000);

    if (!useCuisineAggregationFilter && fetchCuisines) {
      try {
        const response = await fetchCuisines({
          location: selectedLocation.code,
          maximum_num_cuisines: 15,
        });
        const { results } = response?.payload;
        const cuisines = results.map((cuisine) => ({
          name: cuisine,
          icon: <Icon component={KnifeFork} name="knife-fork" />,
        }));
        this.setState({ cuisines });
      } catch (error) {
        Logger.error('[SearchContainer] Error loading cuisine thunk:', error);
        this.setState({ error });
      }
    }

    try {
      if (isAuthenticated) {
        resetFavorites();
        await getMultipleLists({
          lat: userLocation.lat,
          long: userLocation.long,
          limit: 100,
          offset: 0,
        });
      }
    } catch (error) {
      Logger.error('[SearchContainer] Error retrieving lists on mount', error);
      this.setState({ error });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      areExperimentsReady,
      collectionData,
      query,
      selectedDate,
      selectedPartySize,
      selectedTime,
      venueSearchError,
      venueSearchStatus,
    } = this.props;
    const { selectedCollection, urlSearchParams } = this.state;

    if (query && JSON.stringify(query) !== JSON.stringify(prevProps.query)) {
      this.handleQuery(prevProps.query);
    } else if (collectionData.length > 0 && query && query.type === 'collection'
      && (!selectedCollection || selectedCollection.collectionSlug
        !== query.value)) {
      this.setSelectedCollection();
    }

    if ((selectedDate && prevProps.selectedDate && selectedDate
      !== prevProps.selectedDate)
      || (selectedPartySize && prevProps.selectedPartySize && selectedPartySize
        !== prevProps.selectedPartySize)
      || (selectedTime && prevProps.selectedTime && selectedTime
        !== prevProps.selectedTime)) {
      this.search({ page: 1 });
    }

    if (urlSearchParams && urlSearchParams !== prevState.urlSearchParams) {
      this.updateUrl();
    }

    const { pendingInitialSearch } = this.state;

    if (!prevProps.areExperimentsReady && areExperimentsReady && pendingInitialSearch) {
      clearTimeout(this.experimentsHydrationTimeout);
      this.experimentsHydrationTimeout = null;
      this.hasExperimentGateTimedOut = false;
      this.setState({ pendingInitialSearch: false }, this.assessLocation);
    }

    if (venueSearchStatus !== prevProps.venueSearchStatus) {
      if (venueSearchStatus === 'success') {
        this.onSearchSuccess();
      }
      if (venueSearchStatus === 'error' && !!venueSearchError) {
        this.onSearchFailure(venueSearchError);
      }
    }
  }

  componentWillUnmount = () => {
    userLocationMapCookieCleanup();
    initActiveViewCookieCleanup();
    setActiveViewCookieCleanup();
    toggleActiveViewCookieCleanup();
    clearTimeout(this.experimentsHydrationTimeout);
  }

  assessLocation = () => {
    const userLocation = Cookies.get('userLocationMap');
    const state = {
      isLoading: false,
    };

    this.setState(
      () => state,
      () => this.search()
    );

    if (userLocation) {
      navigator.geolocation
        .getCurrentPosition(
          ({ coords }) => {
            const { latitude, longitude } = coords;

            if (this.didCoordinatesChange({
              latitude: state.userLocation.lat,
              longitude: state.userLocation.lng,
            }, { latitude, longitude })) {
              state.userLocation = {
                lat: latitude,
                lng: longitude,
              };

              this.setUserLocationCookie(state.userLocation);

              const coordinates = {
                latitude: state.userLocation.lat,
                longitude: state.userLocation.lng,
              };
              if (this.isWithinLocationRadius(coordinates)) {
                state.locationMode = 'user';
                state.isGeolocationActive = true;
              } else {
                state.locationMode = 'location';
                state.isGeolocationActive = false;
              }

              if (state.locationMode === 'user') {
                this.setState(
                  () => state,
                  () => this.search()
                );
              } else {
                this.setState(state);
              }
            }
          },
          (error) => {
            state.hasGeolocationControlBeenClicked = false;
            state.isGeolocationActive = false;

            if (error.code === 1) {
              state.isGeolocationBlocked = true;
            }

            this.setState(state);
          },
          this.geolocationOptions
        );
    }
  }

  /**
   * 80 meters was chosen because a quick read indicates this is the smaller "standard" for a city block
   * Reference: https://en.wikipedia.org/wiki/City_block#Grid_plan
   */
  didCoordinatesChange = (start, end, threshold, unit) => !haversine(start, end,
    { threshold: (threshold || 80), unit: (unit || 'meter') });

  getBestCenter = () => {
    const {
      center,
      locationMode,
      userLocation,
    } = this.state;

    if (userLocation && locationMode === 'user') {
      return { lat: userLocation.lat, lng: userLocation.lng };
    }

    if (center) {
      return center;
    }

    const { selectedLocation } = this.props;
    const { latitude: lat, longitude: lng } = selectedLocation.map_center;
    return { lat, lng };
  }

  getListImage = (listInfo) => {
    const venueImage = (listInfo?.responsiveImages)
      ? getImageUrl(listInfo?.responsiveImages) : null;

    return venueImage ? (
      <div
        style={{
          backgroundImage: `url(${venueImage})`,
          width: '100%',
          height: '100%',
          backgroundSize: 'cover',
          borderRadius: '10px',
        }}
      />
    ) : (
      <Icon height="30px" width="30px" name="heart" component={FavoriteHeart} />
    );
  };

  handleDesktop = () => {
    const { urlSearchParams: paramsString } = this.state;
    const urlSearchParams = new URLSearchParams(paramsString);
    if (urlSearchParams.has('activeView')) {
      urlSearchParams.delete('activeView');
      this.setState({ urlSearchParams: urlSearchParams.toString() });
    }
  }

  handleGeolocationClick = () => {
    const { hasGeolocationControlBeenClicked } = this.state;
    const state = {
      isGeolocationLoading: true,
    };

    if (!hasGeolocationControlBeenClicked) {
      state.hasGeolocationControlBeenClicked = true;
    }

    this.setState(
      () => state,
      () => navigator.geolocation.getCurrentPosition(this.onGeolocationSuccess,
        this.onGeolocationError, this.geolocationOptions)
    );
  }

  handleMobile = () => {
    const {
      activeView: defaultActiveView,
      urlSearchParams: paramsString,
    } = this.state;
    const urlSearchParams = new URLSearchParams(paramsString);
    let activeView = null;

    /**
     * Priority level
     * query parameter > cookie > default
     */
    if (urlSearchParams.has('activeView')) {
      activeView = urlSearchParams.get('activeView');
    }

    // Query parameter was not present
    if (!activeView) {
      activeView = Cookies.get('activeView');
    }

    // Query parameter and cookie were not present, set to default
    if (!activeView) {
      activeView = defaultActiveView;
    }

    urlSearchParams.set('activeView', activeView);
    this.setState({ activeView, urlSearchParams: urlSearchParams.toString() });
    initActiveViewCookieCleanup = setFunctionalCookie(
      'activeView', activeView, { expires: moment().add(30, 'days').toDate() }
    );
  }

  handleQuery = (prevQuery) => {
    const { collectionData, query, selectedLocation } = this.props;
    if (query.type !== 'collection') {
      this.setState({ selectedCollection: null });
    } else if (collectionData.length > 0) {
      this.setSelectedCollection();
    }

    const isListFacet = !!(query.type === QUERY_TYPE.facet
      && query.value.indexOf(FACET_TYPE.list) > -1);
    const isQueryWithNewValue = !!(query.type === QUERY_TYPE.query
      && query.value !== '' && query.value !== prevQuery.value);
    const isReset = !!(query.type === QUERY_TYPE.query && query.value === ''
      && query.value !== prevQuery.value);

    if ((isListFacet) || (isQueryWithNewValue)) {
      this.setShouldUseBoundingBoxAndSearch(false);
    } else if ((isReset)) {
      this.setState({
        hasUserInteractedWithMap: false,
        locationMode: 'location',
        page: 1,
        shouldShowRedo: false,
        shouldUseBoundingBox: false,
        boundingBox: null,
        center: {
          lat: selectedLocation.map_center.latitude,
          lng: selectedLocation.map_center.longitude,
        },
      }, () => {
        this.search();
      });
    } else {
      const { hasUserInteractedWithMap } = this.state;
      this.setShouldUseBoundingBoxAndSearch(hasUserInteractedWithMap);
    }
  }

  onClear = () => {
    const { onClear } = this.props;

    onClear();
  }

  handleExpandToVenues = (mapRef) => {
    // Note: setting map zoom to maximum to capture all venues across countries
    const newZoom = 1;
    mapRef.setZoom(newZoom);
    const bounds = mapRef.getBounds();
    const state = {
      boundingBox: bounds,
      shouldShowRedo: false,
      shouldUseBoundingBox: true,
      zoom: newZoom,
    };

    this.setState(state, () => {
      this.setShouldUseBoundingBoxAndSearch(true);
    });
  }

  handleExpandMap = (mapRef) => {
    const { zoom: prevZoom } = this.state;
    const newZoom = prevZoom - 2;
    mapRef.setZoom(newZoom);
    const bounds = mapRef.getBounds();
    const state = {
      boundingBox: bounds,
      shouldShowRedo: false,
      shouldUseBoundingBox: true,
      zoom: newZoom,
    };
    this.setState(state, () => {
      this.setShouldUseBoundingBoxAndSearch(true);
    });
  }

  isWithinLocationRadius = (coordinates) => {
    const { selectedLocation } = this.props;
    const { latitude, longitude } = selectedLocation.map_center;
    const selectedLocationCenter = { latitude, longitude };
    return !this.didCoordinatesChange(selectedLocationCenter, coordinates,
      selectedLocation.radius, 'mile');
  }

  onInfoBoxSelect = (venue) => {
    const { onVenueSelect } = this.props;
    onVenueSelect(venue, 'Info Box');
  }

  onGeolocationActiveChange = () => this.setState({ isGeolocationActive: true });

  onGeolocationSuccess = (position) => {
    const {
      // eslint-disable-next-line no-shadow
      $rootScope,
      $window,
      nearbyTranslation,
      selectedLocation,
      getMarketLocationThunk,
      setSelectedLocationThunk,
    } = this.props;
    const { latitude: lat, longitude: lng } = position.coords;
    const state = {
      hasUserInteractedWithMap: false,
      isGeolocationActive: true,
      isGeolocationLoading: false,
      locationMode: 'user',
      page: 1,
      shouldShowRedo: false,
      shouldUseBoundingBox: false,
      userLocation: {
        lat,
        lng,
      },
      defaultCenter: position.coords,
    };

    this.setUserLocationCookie(state.userLocation);
    this.setState(
      () => state,
      () => this.search()
    );

    const coordinates = {
      latitude: lat,
      longitude: lng,
    };

    if (!this.isWithinLocationRadius(coordinates)) {
      getMarketLocationThunk({ coordinates: state.userLocation })
        .then(
          (location) => {
            const params = { $rootScope, $window };
            if (location) {
              params.location = location;
            } else {
              params.location = {
                ...selectedLocation,
                name: nearbyTranslation,
                translationKey: 'nearby',
              };
            }
            setSelectedLocationThunk(params);
          },
          () => {
          }
        );
    }
  }

  onGeolocationError = (error) => {
    const newError = {
      code: error.code,
      message: error.message,
      shouldHide: false,
    };

    if (newError.code === 1) {
      newError.message = 'Geolocation is blocked. Please check your browser\'s settings and try again.'; // eslint-disable-line no-param-reassign
    } else if (newError.code === 2) {
      newError.message = 'Something went wrong. Please try again.'; // eslint-disable-line no-param-reassign
    } else if (newError.code === 3) {
      newError.message = 'Geolocation timed out. Please try again.'; // eslint-disable-line no-param-reassign
    }

    const state = {
      error: newError,
      hasGeolocationControlBeenClicked: false,
      isGeolocationLoading: false,
    };

    if (newError.code === 1) {
      state.isGeolocationBlocked = true;
    }

    this.setState(state);

    setTimeout(() => {
      newError.shouldHide = true;
      this.setState({ error: newError });
    }, 3000);
  }

  onMarkerSelect = (venue) => {
    if (venue) {
      this.setState({
        selectedVenue: venue.id,
        hoveredVenue: null,
      });
    } else {
      this.setState({ selectedVenue: null });
    }
  }

  onPageChange = ({ selected }) => {
    this.setState(
      () => ({ page: selected + 1 }),
      () => this.search()?.then(() => {
        // Move focus to first search result after pagination. @see CON-1578
        (document.getElementsByClassName(SEARCH_ITEM_LINK_CLASS)[0])?.focus();
      })
    );
  }

  onPillSelect = (pill) => {
    const { onPillSelect } = this.props;
    this.setState({ selectedCollection: null });
    onPillSelect(pill);
  }

  onRedo = (isUserAction = false) => {
    this.setShouldUseBoundingBoxAndSearch(true, (searchSessionId) => {
      if (isUserAction) {
        const { Analytics } = this.props;
        Analytics.event('complete search', {
          context: utils.getPageContext(window.location.pathname),
          'filter type': 'search here',
          value: 'search here',
          searchSessionId,
        });
      }
    });
  }

  onScroll = () => {
    window.removeEventListener('scroll', this.onScroll, true);
  }

  onSearchResultSelect = (venue) => {
    const { onVenueSelect } = this.props;
    onVenueSelect(venue, 'Search Result');
  }

  onSearchSuccess = () => {
    const {
      $rootScope,
      hits,
      query,
      selectedLocation,
    } = this.props;

    if (query?.type === 'facet') {
      const key = query.value.split(':')[0];
      const cuisine = query?.value?.split(':')[1];
      $rootScope.metadata.title = getTranslation({
        translationKey: 'search_facet',
        translationValues: { cuisine },
        fallback: `Discover the Best ${cuisine} Restaurants on Resy`,
      });

      $rootScope.metadata.description = getTranslation({
        translationKey: 'descriptions.search_facet',
        translationValues: {
          cuisine,
          locationName: selectedLocation.name,
        },
        fallback: `Looking for the essential ${cuisine} restaurants in ${selectedLocation.name}? Search and book them right now on Resy.`,
      });

      if (key === FACET_TYPE.list) {
        $rootScope.metadata.title = getTranslation({
          translationKey: 'lists.search_facet_title',
          fallback: 'Discover the Best Restaurants with Resy Lists',
        });

        $rootScope.metadata.description = getTranslation({
          translationKey: 'lists.search_facet_description',
          translationValues: {
            locationName: selectedLocation.name,
          },
          fallback: `Looking for the essential restaurants in ${selectedLocation.name}? Search and book them right now on Resy.`,
        });
      }
    }

    const { selectedVenue } = this.state;
    const state = {};

    if (selectedVenue) {
      const foundVenue = hits.find((hit) => hit.id === selectedVenue);
      if (!foundVenue) {
        state.selectedVenue = null;
      }
    }

    this.setState(state);
  }

  onSearchFailure = (err) => {
    const { isAuthenticated, showAuthModal, $rootScope } = this.props;
    const UNAUTHORIZED = 419;

    if (err.status === UNAUTHORIZED) {
      // authenticated user trying to access an unauthorized resource
      if (isAuthenticated) {
        window.location = '/';
        return;
      }

      $rootScope.nav.backPath = `${window.location.pathname}${window.location.search}`;
      showAuthModal();
    }

    Logger.error('[SearchContainer] error', err);
  }

  onVenueCardHover = (id) => {
    this.setState({
      hoveredVenue: id,
      selectedVenue: null,
    });
  }

  search = (additionalParams = {}, analyticsFn) => {
    const {
      // eslint-disable-next-line no-shadow
      areExperimentsReady,
      getVenueSearchThunk,
      isSearching,
      query,
      selectedCoreISODate,
      selectedCoreSeats,
      selectedLocation,
      selectedTime,
      Analytics,
    } = this.props;
    const {
      boundingBox,
      hitsPerPage,
      page,
      pendingInitialSearch,
      selectedCollection,
      shouldUseBoundingBox,
    } = this.state;

    if (!areExperimentsReady && !this.hasExperimentGateTimedOut) {
      if (!pendingInitialSearch) {
        this.setState({ pendingInitialSearch: true });
      }
      return Promise.resolve();
    }

    if (isSearching) {
      return undefined;
    }

    const state = {
      page: additionalParams.page || page,
    };

    const params = {
      hitsPerPage,
      page,
      query: {
        ...query,
        id: selectedCollection?.type_id,
      },
      selectedLocationCode: selectedLocation.code,
      ...additionalParams,
    };

    if (shouldUseBoundingBox && boundingBox) {
      const {
        south,
        west,
        north,
        east,
      } = boundingBox.toJSON();
      params.boundingBox = [south, west, north, east];
    } else {
      const { lat, lng } = this.getBestCenter();
      params.latitude = lat;
      params.longitude = lng;
      params.radius = selectedLocation.radius * 1610;
      state.boundingBox = null;
    }

    this.setState(state);

    params.selectedDate = moment(selectedCoreISODate, ISO_DATE_FORMAT).toDate();
    params.selectedPartySize = selectedCoreSeats;
    params.timeFilter = (selectedTime === ALL_DAY || !selectedTime) ? undefined
      : moment(selectedTime, 'HHmm').format('HH:mm');
    return getVenueSearchThunk(params)
      .then(({ payload: { meta, metadata } }) => {
        analyticsFn?.(metadata.searchSessionId);
        Analytics.event('view search page', {
          searchSessionId: metadata.searchSessionId,
          'page count': meta?.total_pages,
          'page number': meta?.page,
          'total result count': meta?.total,
          engine: meta?.engine,
          'results per page': meta?.per_page,
        });
      })
      .catch((err) => {
        const message = (err && err.data && err.data.message) || err.status;
        Logger.error(`[SearchContainer] search error: ${message}`);
      });
  }

  setScrollListener = () => window.addEventListener('scroll', this.onScroll,
    true);

  setSelectedCollection = (currentCollection) => {
    const { collectionData, query, searchSessionId } = this.props;
    const selectedCollection = currentCollection || collectionData.find(
      (collection) => collection.collectionSlug === query.value
    );

    if (selectedCollection) {
      const { $rootScope, Analytics } = this.props;
      this.setState({ selectedCollection });

      if (selectedCollection.type_id === 10) {
        selectedCollection.name = getTranslation({
          translationKey: 'gda_search_title',
          fallback: 'Resy’s Sought-After Restaurants',
        });
        selectedCollection.description = getTranslation({
          translationKey: 'gda_search_description',
          fallback: 'These restaurants hold reservations exclusively for eligible American Express<sup>®</sup> Card Members.',
        });
        selectedCollection.eyebrow = getTranslation({
          translationKey: 'gda_eyebrow',
          fallback: 'Resy Global Dining Access',
        });
        selectedCollection.learnMore = getTranslation({
          translationKey: 'gda_description_learn_more',
          fallback: 'Learn more',
        });
        selectedCollection.learnMoreLink = GDA_URL;
      }
      $rootScope.metadata.title = getTranslation({
        translationKey: 'search_collection',
        translationValues: { collectionName: selectedCollection.name },
        fallback: `${selectedCollection.name} on Resy`,
      });
      $rootScope.metadata.description = selectedCollection.description;
      Analytics.event('complete search', {
        context: utils.getPageContext(window.location.pathname),
        'filter type': 'collection',
        value: selectedCollection.name,
        searchSessionId,
      });
    }
  };

  setShouldUseBoundingBoxAndSearch = (shouldUseBoundingBox, analyticsFn) => {
    const state = {
      locationMode: 'location',
      page: 1,
      shouldShowRedo: false,
      shouldUseBoundingBox,
    };

    /**
     * Switching back to using center coordinates, so update them based on last boundingBox.
     * - boundingBox is updated via map's onIdle, so it is expected they are the accurate current state
     */
    if (!shouldUseBoundingBox) {
      const { boundingBox } = this.state;
      if (boundingBox) {
        const boundsCenter = boundingBox.getCenter();
        state.center = {
          lat: boundsCenter.lat(),
          lng: boundsCenter.lng(),
        };
      }
    }

    this.setState(
      () => state,
      () => {
        this.search({}, analyticsFn);
      }
    );
  }

  setUserLocationCookie = (userLocation) => {
    userLocationMapCookieCleanup = setFunctionalCookie(
      'userLocationMap', userLocation,
      { expires: moment().add(1, 'days').toDate() }
    );
  }

  setViewToList = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    urlSearchParams.set('activeView', 'list');
    this.setState(
      { activeView: 'list', urlSearchParams: urlSearchParams.toString() }
    );
    setActiveViewCookieCleanup = setFunctionalCookie(
      'activeView', 'list', { expires: moment().add(30, 'days').toDate() }
    );
  }

  toggleActiveView = () => {
    const { activeView: oldActiveView } = this.state;
    const urlSearchParams = new URLSearchParams(window.location.search);
    const activeView = oldActiveView === 'list' ? 'map' : 'list';
    urlSearchParams.set('activeView', activeView);
    this.setState({ activeView, urlSearchParams: urlSearchParams.toString() });
    toggleActiveViewCookieCleanup = setFunctionalCookie(
      'activeView', activeView, { expires: moment().add(30, 'days').toDate() }
    );
  }

  transformLists = (lists) => {
    const listItems = lists && lists.map((currentList) => {
      const {
        count, createdBy, listId, name,
      } = currentList;

      return {
        count: getNumOfVenuesContent(count),
        createdBy,
        id: listId,
        name,
        image: this.getListImage(currentList),
      };
    });
    return listItems;
  };

  updateMapProperties = (boundingBox, zoom, preventGeolocationActiveChange) => {
    const { boundingBox: prevBoundingBox } = this.state;
    const state = { boundingBox, zoom };

    if (prevBoundingBox && !boundingBox.equals(prevBoundingBox)) {
      if (!preventGeolocationActiveChange) {
        state.isGeolocationActive = false;
      }
      state.hasUserInteractedWithMap = true;
      state.shouldShowRedo = true;
    }

    this.setState(state);
  }

  updateUrl = () => {
    const { urlSearchParams } = this.state;
    window.history.replaceState({}, '',
      `${window.location.pathname}?${urlSearchParams}`);
  }

  render() {
    const {
      activeView,
      cuisines: stateCuisines,
      error,
      hasGeolocationControlBeenClicked,
      hitsPerPage,
      hoveredVenue,
      isGeolocationActive,
      isGeolocationBlocked,
      isGeolocationLoading,
      isLoading,
      locationMode,
      page,
      selectedCollection,
      selectedVenue,
      shouldShowRedo,
      shouldUseBoundingBox,
      pendingInitialSearch,
      userLocation,
    } = this.state;
    const {
      $rootScope,
      $window,
      Analytics,
      collectionData,
      cuisines: rawCuisines,
      favoriteVenueIds,
      hits,
      isAuthenticated,
      isMobile,
      isSearching,
      lists,
      nbPages,
      nearbyTranslation,
      query,
      reservationLimit,
      selectedDate,
      selectedTime,
      selectedLocation,
      selectedPartySize,
      showAuthModal,
      setQueuedData,
      store,
      getQueuedData,
      useCuisineAggregationFilter,
    } = this.props;
    const isSearchUiPending = isSearching || pendingInitialSearch;

    const cuisines = useCuisineAggregationFilter
      ? rawCuisines.map((name) => ({
        name,
        icon: <Icon component={KnifeFork} name="knife-fork" />,
      }))
      : stateCuisines;

    const seating = [
      {
        name: getTranslation(
          { translationKey: 'seating_indoor', fallback: 'Indoor' }
        ),
        value: 'Indoor',
        icon: <Icon component={Table} name="table" />,
      },
      {
        name: getTranslation(
          { translationKey: 'seating_outdoor', fallback: 'Outdoor' }
        ),
        value: 'Outdoor',
        icon: <Icon component={Table} name="table" />,
      },
    ];

    const defaultCenter = {
      lat: selectedLocation.map_center.latitude,
      lng: selectedLocation.map_center.longitude,
    };
    let shouldCenterOnUser = false;

    if (locationMode === 'user' && hits.length === 0) {
      shouldCenterOnUser = true;
    }
    return (
      <div
        className={cx('SearchContainer', {
          [`SearchContainer--${activeView}`]: isMobile && activeView,
        })}
      >
        <SearchResultsContainer
          $rootScope={$rootScope}
          Analytics={Analytics}
          cuisines={cuisines}
          collectionData={collectionData}
          event={Analytics.event}
          favoriteVenueIds={favoriteVenueIds}
          getQueuedData={getQueuedData}
          handlePinState={this.handlePinState}
          hitsPerPage={hitsPerPage}
          hits={hits}
          isMobile={isMobile}
          isSearching={isSearchUiPending}
          lists={this.transformLists(lists)}
          nbPages={nbPages}
          onClear={this.onClear}
          onVenueCardHover={this.onVenueCardHover}
          onPageChange={this.onPageChange}
          onPillSelect={this.onPillSelect}
          onSearchResultSelect={this.onSearchResultSelect}
          page={page}
          query={query}
          reservationLimit={reservationLimit}
          seating={seating}
          selectedCollection={selectedCollection}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          selectedLocation={selectedLocation}
          selectedPartySize={selectedPartySize}
          selectedVenueId={selectedVenue}
          setQueuedData={setQueuedData}
          showAuthModal={showAuthModal}
          store={store}
          toggleActiveView={this.toggleActiveView}
        />
        <ABTestWithFallback flagKey="resy_web-use-new_google_map_package" fallbackKey="off" variationKeyList={['on', 'off']}>
          <Variation variationKey="on">
            <APIProvider apiKey="AIzaSyC0jiw4qcNABSxhZykpJebtsP-Qf1oz6Tc">
              <MapContainer
                $rootScope={$rootScope}
                $window={$window}
                Analytics={Analytics}
                cuisines={cuisines}
                defaultCenter={defaultCenter}
                error={error}
                googleMapURL="https://maps.googleapis.com/maps/api/js?v=quarterly&key=AIzaSyC0jiw4qcNABSxhZykpJebtsP-Qf1oz6Tc"
                hasGeolocationControlBeenClicked={hasGeolocationControlBeenClicked}
                hits={hits}
                hoveredVenue={hoveredVenue}
                isAuthenticated={isAuthenticated}
                isActive={activeView === 'map' || !isMobile}
                isGeolocationActive={isGeolocationActive}
                isGeolocationBlocked={isGeolocationBlocked}
                isGeolocationLoading={isGeolocationLoading}
                isGeolocationSupported={this.isGeolocationSupported}
                isLoading={isLoading}
                isMobile={isMobile}
                isSearching={isSearchUiPending}
                lists={this.transformLists(lists)}
                loadingElement={<div />}
                mapElement={<div className="Map" />}
                nearbyTranslation={nearbyTranslation}
                onActiveViewToggle={this.toggleActiveView}
                onClear={this.onClear}
                onExpandToVenues={this.handleExpandToVenues}
                onExpandMap={this.handleExpandMap}
                onGeolocationActiveChange={this.onGeolocationActiveChange}
                onGeolocationClick={this.handleGeolocationClick}
                onInfoBoxSelect={this.onInfoBoxSelect}
                onMapChange={this.updateMapProperties}
                onMarkerSelect={this.onMarkerSelect}
                onRedo={this.onRedo}
                query={query}
                seating={seating}
                selectedDate={selectedDate}
                selectedLocation={selectedLocation}
                selectedPartySize={selectedPartySize}
                selectedTime={selectedTime === ALL_DAY ? undefined : selectedTime}
                selectedVenueId={selectedVenue}
                shouldCenterOnUser={shouldCenterOnUser}
                shouldFitBounds={!shouldUseBoundingBox}
                shouldShowRedo={shouldShowRedo}
                userLocation={userLocation}
                urlSlug={selectedLocation.url_slug}
              />
            </APIProvider>
          </Variation>
          <Variation variationKey="off">
            <DeprecatedMapContainer
              $rootScope={$rootScope}
              $window={$window}
              Analytics={Analytics}
              containerElement={<div className="MapContainer" data-test-id="deprecated_map_container" />}
              cuisines={cuisines}
              defaultCenter={defaultCenter}
              error={error}
              googleMapURL="https://maps.googleapis.com/maps/api/js?v=quarterly&key=AIzaSyC0jiw4qcNABSxhZykpJebtsP-Qf1oz6Tc"
              hasGeolocationControlBeenClicked={hasGeolocationControlBeenClicked}
              hits={hits}
              hoveredVenue={hoveredVenue}
              isAuthenticated={isAuthenticated}
              isActive={activeView === 'map' || !isMobile}
              isGeolocationActive={isGeolocationActive}
              isGeolocationBlocked={isGeolocationBlocked}
              isGeolocationLoading={isGeolocationLoading}
              isGeolocationSupported={this.isGeolocationSupported}
              isLoading={isLoading}
              isMobile={isMobile}
              isSearching={isSearchUiPending}
              lists={this.transformLists(lists)}
              loadingElement={<div />}
              mapElement={<div className="Map" />}
              nearbyTranslation={nearbyTranslation}
              onActiveViewToggle={this.toggleActiveView}
              onClear={this.onClear}
              onExpandToVenues={this.handleExpandToVenues}
              onExpandMap={this.handleExpandMap}
              onGeolocationActiveChange={this.onGeolocationActiveChange}
              onGeolocationClick={this.handleGeolocationClick}
              onInfoBoxSelect={this.onInfoBoxSelect}
              onMapChange={this.updateMapProperties}
              onMarkerSelect={this.onMarkerSelect}
              onRedo={this.onRedo}
              query={query}
              seating={seating}
              selectedDate={selectedDate}
              selectedLocation={selectedLocation}
              selectedPartySize={selectedPartySize}
              selectedTime={selectedTime === ALL_DAY ? undefined : selectedTime}
              selectedVenueId={selectedVenue}
              shouldCenterOnUser={shouldCenterOnUser}
              shouldFitBounds={!shouldUseBoundingBox}
              shouldShowRedo={shouldShowRedo}
              userLocation={userLocation}
              urlSlug={selectedLocation.url_slug}
            />
          </Variation>
        </ABTestWithFallback>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const {
    favorite,
    resyCore: {
      selectedCoreISODate,
      selectedCoreSeats,
    },
    user,
    venueSearch,
    venueSearch: {
      search: {
        cuisines, hits, nbPages, status, error,
      },
    },
  } = state;
  const { enabled: useCuisineAggregationFilter = false } = selectExperiment(state, RESY_WEB_ENABLE_CUISINE_AGGREGATION_FILTER);
  return {
    areExperimentsReady: selectExperimentsReady(state),
    cuisines,
    useCuisineAggregationFilter,
    hits,
    isAuthenticated: selectIsAuthenticated(state),
    isSearching: status === 'pending',
    lists: favorite.lists,
    nbPages,
    selectedCoreISODate,
    selectedCoreSeats,
    searchSessionId: venueSearch?.metadata?.searchSessionId,
    userLocation: user.userLocation,
    venueSearchStatus: status,
    venueSearchError: error,
  };
};

const mapDispatchToProps = {
  getMarketLocationThunk,
  getMultipleLists,
  getVenueSearchThunk,
  loadCuisinesThunk,
  resetFavorites,
  setSelectedLocationThunk,
};

SearchContainer.displayName = 'SearchContainer';
export {
  SearchContainer as ComponentUnderTest,
  searchContainerPropTypes,
  mapStateToProps,
};
export default connect(mapStateToProps, mapDispatchToProps)(SearchContainer);
